import { useCallback, useMemo, useState } from "react";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { z } from "zod";
import {
  Box,
  Skeleton,
  Text,
  useWidgetInstance,
  useWidgetViewState,
  widgetCardDomId,
  type DataTableGroupSortDirection,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useEntityList, useEntityListTotal } from "../entity-list/use-entity-list";
import { resolveStoredTitle } from "../shared/derived-titles";
import { IssueList } from "../shared/issue-list";
import { IssueTable } from "../shared/issue-table";
import {
  AssignedCustomizePanel,
  type AssignedCustomizePage,
  type AssignedCustomizeValue,
} from "./assigned-customize";
import { AssignedToolbar } from "./assigned-toolbar";
import {
  ASSIGNED_TITLE,
  ASSIGNED_TITLE_FAMILY,
  assignedSortDirection,
  useProjectTypeWhere,
  type AssignedSort,
} from "./use-assigned-list";

/**
 * `assigned-list` — the viewer's own projects: My Work's "Assigned to me"
 * card.
 *
 * The body is the scoped, server-paged project set the `entity-list` home card
 * renders (`IssueTable` in `home` mode, or `IssueList` under the `list`
 * layout), under the full-page table's toolbar band: Sort by, search and the
 * Customize view gear on the right. The sort is a per-viewer lens seeded from
 * the saved config and held in view-state; the Customize view's rows — the
 * Type filter and the saved sort — are the config, and each saves on change,
 * because My Work is the member's own board and the config IS the view.
 *
 * A **managed-only** card (the My Work catalog) — it contributes no `catalog`
 * entry, so it never appears in the free Add-card gallery.
 */
export const assignedListConfigSchema = z.looseObject({
  /** The Filters page: project type NAMES; empty reads as every type. */
  types: z.array(z.string()).default([]),
  sort: z.enum(["created", "updated", "name"]).default("updated"),
  // Table by default — the inventory is a table, and in fill layout it holds
  // its columns at the board's half width. The row list stays a setting.
  layout: z.enum(["table", "list"]).default("table"),
});

export type AssignedListConfig = z.infer<typeof assignedListConfigSchema>;

/** The per-viewer lens over the card — the sort the toolbar set with its
 *  direction, seeded from Settings and never persisted from here. */
type AssignedListViewState = {
  sort?: AssignedSort;
  sortDirection?: DataTableGroupSortDirection;
};

/** The total the title wears: the server's exact project count. A short bar
 *  holds its width until it answers so the title does not jump. */
function AssignedTitleCount({ config }: WidgetRenderContext<AssignedListConfig>) {
  const { total, loading } = useEntityListTotal(config.sort, undefined);
  if (loading) return <Skeleton height={10} width={28} />;
  return (
    <Text size="xs" weight="medium" variant="muted">
      · {total.toLocaleString()}
    </Text>
  );
}

/** The card's body: the scoped, server-paged table or row list — the
 *  `entity-list` home card's body, minus its header controls. */
function AssignedProjects({
  sort,
  direction,
  layout,
  search,
  types,
  w,
}: {
  sort: AssignedSort;
  direction: DataTableGroupSortDirection;
  layout: AssignedListConfig["layout"];
  search: string;
  /** The Type filter, by name. */
  types: readonly string[];
  w: number;
}) {
  const { where: scopedWhere, orderBy } = useEntityList(sort, undefined, search, direction);
  const typeWhere = useProjectTypeWhere(types);
  const where = useMemo(
    () => (typeWhere ? combineWhereConditions([scopedWhere ?? {}, typeWhere]) : scopedWhere),
    [scopedWhere, typeWhere],
  );
  const tableQuery = useMemo(() => ({ where, orderBy }), [where, orderBy]);
  const emptyTitle = useWindowEmptyTitle("No Projects");
  if (layout === "list") {
    return <IssueList query={tableQuery} emptyTitle={emptyTitle} />;
  }
  return (
    <IssueTable query={tableQuery} w={w} emptyTitle={emptyTitle} groupBy="none" home />
  );
}

function AssignedListRender({ w, config, onConfigChange }: WidgetRenderContext<AssignedListConfig>) {
  const { state, patch } = useWidgetViewState();
  const stored = state as AssignedListViewState;
  const sort = stored.sort ?? config.sort;
  const sortDirection = stored.sortDirection ?? assignedSortDirection(sort);
  const handleLensChange = useCallback(
    (next: AssignedListViewState) => patch(next),
    [patch],
  );
  const [searchValue, setSearchValue] = useState("");
  // Search reaches the server, so it is debounced — one query per pause, not
  // per keystroke.
  const search = useDebouncedValue(searchValue, 300);

  // The card's Customize view pins over the whole card, header included: the
  // kit stamps every placed card's root with `widgetCardDomId`, and the sheet
  // measures that.
  const instance = useWidgetInstance();
  const anchorId = widgetCardDomId(instance?.instanceId ?? "solo");
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizePage, setCustomizePage] = useState<AssignedCustomizePage>("main");
  const openCustomize = useCallback(() => {
    setCustomizePage("main");
    setCustomizeOpen(true);
  }, []);
  const customizeValue: AssignedCustomizeValue = {
    types: config.types,
    sort,
    sortDirection,
  };
  // The drawer's rows are the SAVED settings: each writes the card's config
  // (the keys Settings edits) and clears the per-viewer lens it would
  // otherwise sit behind, so the card shows what the drawer says.
  const handleCustomizeChange = useCallback(
    (next: Partial<AssignedCustomizeValue>) => {
      const configPatch: Partial<AssignedListConfig> = {};
      if (next.types) configPatch.types = [...next.types];
      if (next.sort) configPatch.sort = next.sort;
      if (Object.keys(configPatch).length > 0) onConfigChange?.(configPatch);
      const lensPatch: AssignedListViewState = {};
      if (next.sort) lensPatch.sort = next.sort;
      if (next.sortDirection) lensPatch.sortDirection = next.sortDirection;
      if (Object.keys(lensPatch).length > 0) patch(lensPatch);
    },
    [onConfigChange, patch],
  );

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <AssignedToolbar
        defaultSort={config.sort}
        onCustomize={openCustomize}
        onSearchChange={setSearchValue}
        onSortChange={(nextSort, nextDirection) =>
          handleLensChange({ sort: nextSort, sortDirection: nextDirection })
        }
        onSortDirectionChange={(nextDirection) => handleLensChange({ sortDirection: nextDirection })}
        searchValue={searchValue}
        sort={sort}
        sortDirection={sortDirection}
      />
      <AssignedProjects
        direction={sortDirection}
        layout={config.layout}
        search={search}
        sort={sort}
        types={config.types}
        w={w}
      />
      <AssignedCustomizePanel
        anchorId={anchorId}
        canEdit={onConfigChange != null}
        onChange={handleCustomizeChange}
        onOpenChange={setCustomizeOpen}
        onPageChange={setCustomizePage}
        open={customizeOpen}
        page={customizePage}
        value={customizeValue}
      />
    </Box>
  );
}

const assignedListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "layout",
        kind: "select",
        label: "Layout",
        icon: "table",
        options: [
          { value: "table", label: "Table" },
          { value: "list", label: "List" },
        ],
      },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "created", label: "Newest first" },
          { value: "updated", label: "Recently updated" },
          { value: "name", label: "Name (A–Z)" },
        ],
      },
    ],
  },
];

export const assignedListDef: WidgetModule<AssignedListConfig> = {
  type: "assigned-list",
  title: ASSIGNED_TITLE,
  // A stored title the card itself generated re-derives; a member's own title
  // stands.
  dynamicTitle: (config) =>
    resolveStoredTitle(config, (title) => ASSIGNED_TITLE_FAMILY.has(title), ASSIGNED_TITLE),
  icon: "user-check",
  // Table-first: the home card maximizes straight to the data view, which is
  // the scoped projects at full size.
  detailPresentation: "data",
  // Full-bleed body for the table (row dividers + selection wash reach the
  // card edge); the row list restores the inset itself.
  body: "bleed",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 12 },
  configSchema: assignedListConfigSchema,
  titleAdornment: AssignedTitleCount,
  settings: assignedListSettings,
  render: AssignedListRender,
};
